import React from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";

interface SkeletonProps {
  variant?: "text" | "rect" | "circle";
  width?: number | string;
  height?: number | string;
  animation?: "pulse" | "wave" | false;
  className?: string;
  style?: React.CSSProperties;
}

const useStyles = makeStyles<Theme, SkeletonProps>((theme) => ({
  root: {
    display: "block",
    backgroundColor: theme.palette.action.hover,
    height: ({ variant, height }) => {
      if (height) {
        return height;
      }
      return variant === "text" ? "1.2em" : 118;
    },
    width: ({ width }) => width || "100%",
    borderRadius: ({ variant }) => {
      if (variant === "circle") {
        return "50%";
      }
      if (variant === "text") {
        return theme.shape.borderRadius / 2;
      }
      return theme.shape.borderRadius;
    },
  },
  pulse: {
    animation: "$pulse 1.5s ease-in-out 0.5s infinite",
  },
  wave: {
    position: "relative",
    overflow: "hidden",
    "&::after": {
      animation: "$wave 1.6s linear 0.5s infinite",
      background: `linear-gradient(90deg, transparent, ${theme.palette.common.white}, transparent)`,
      content: '""',
      position: "absolute",
      transform: "translateX(-100%)",
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
    },
  },
  "@keyframes pulse": {
    "0%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.4,
    },
    "100%": {
      opacity: 1,
    },
  },
  "@keyframes wave": {
    "0%": {
      transform: "translateX(-100%)",
    },
    "50%": {
      transform: "translateX(100%)",
    },
    "100%": {
      transform: "translateX(100%)",
    },
  },
}));

export default function Skeleton({
  variant = "text",
  width,
  height,
  animation = "wave",
  className,
  style,
  ...props
}: Readonly<SkeletonProps>) {
  const classes = useStyles({ variant, width, height });

  let animationClass = "";
  if (animation === "pulse") {
    animationClass = classes.pulse;
  } else if (animation === "wave") {
    animationClass = classes.wave;
  }

  return <Box className={`${classes.root} ${animationClass} ${className || ""}`} style={style} {...props} />;
}
